import { api } from "@/lib/api";
import { auth } from "@/lib/auth";
import {
  mapWorkerFromDto,
  WorkerDto,
  workerDtoListSchema,
  WorkerDtoSchema,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getPersonalInfoQueryOptions = queryOptions({
  queryKey: ["personal-info"],
  queryFn: async () =>
    api
      .get("/api/workers", workerDtoListSchema, {
        projection: ["Person"],
        filters: {
          "Person.Email": (await auth.user)?.profile.email,
        },
      })
      .then((res) => {
        if (res.Data.length === 0) {
          throw new Error("No worker found with this email");
        }
        return mapWorkerFromDto(res.Data[0]);
      }),
});

export function useGetPersonalInfo() {
  const q = useQuery(getPersonalInfoQueryOptions);
  useEffect(() => {
    if (!q.error) return;
    auth.logout();
  }, [q.error]);
  return q;
}

export const getWorkersQueryOptions = queryOptions({
  queryKey: ["workers"],
  queryFn: async () => {
    const result: Array<WorkerDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/workers", workerDtoListSchema, {
        query: {
          page: page.toString(),
        },
        projection: ["Person"],
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(mapWorkerFromDto);
  },
});

export function useGetWorkers<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getWorkersQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getWorkersError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getWorkerQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["workers", id],
    queryFn: () =>
      api
        .get("/api/workers/$id", WorkerDtoSchema, {
          pathReplace: { $id: id },
          projection: ["Person"],
        })
        .then(mapWorkerFromDto),
  });
}

export function useGetWorker<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({ ...getWorkerQueryOptions(id), enabled: opts?.enabled });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getWorkerDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
